import { useState, useEffect } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { adminAPI } from '@/lib/api';
import { Users, TrendingUp, CreditCard, BarChart3, Activity, DollarSign, UserCheck, Clock } from 'lucide-react';
import { BarChart, Bar, LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, AreaChart, Area, PieChart, Pie, Cell } from 'recharts';
import { useTheme } from '@/context/ThemeContext';

const formatCurrency = (v) => v == null ? '$0' : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(v);

export default function AdminDashboardPage() {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const { theme } = useTheme();

  useEffect(() => {
    adminAPI.getAnalytics().then(r => { setAnalytics(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  if (loading) return <AdminLayout><div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-green-500 border-t-transparent rounded-full animate-spin" /></div></AdminLayout>;

  const a = analytics || {};
  const isDark = theme === 'dark';

  const stats = [
    { label: 'Total Users', value: a.total_users || 0, icon: Users, color: 'blue', change: '+12%' },
    { label: 'Active Users', value: a.active_users || 0, icon: UserCheck, color: 'emerald', change: '+8%' },
    { label: 'Total Trades', value: a.total_trades || 0, icon: TrendingUp, color: 'violet', change: '+24%' },
    { label: 'Open Trades', value: a.open_trades || 0, icon: Activity, color: 'amber', change: '' },
    { label: 'Trading Volume', value: formatCurrency(a.total_volume), icon: BarChart3, color: 'blue', change: '+18%' },
    { label: 'Total Revenue', value: formatCurrency(a.total_revenue), icon: DollarSign, color: 'emerald', change: '+15%' },
    { label: 'Pending Deposits', value: a.pending_deposits || 0, icon: CreditCard, color: 'amber', change: '' },
    { label: 'Pending Withdrawals', value: a.pending_withdrawals || 0, icon: Clock, color: 'red', change: '' },
  ];

  const colorMap = { blue: 'bg-green-500/15 text-green-400', emerald: 'bg-emerald-500/15 text-emerald-400', violet: 'bg-violet-500/15 text-violet-400', amber: 'bg-amber-500/15 text-amber-400', red: 'bg-red-500/15 text-red-400' };

  const barData = [
    { name: 'Mon', trades: 45, volume: 12000 },
    { name: 'Tue', trades: 62, volume: 18000 },
    { name: 'Wed', trades: 38, volume: 9500 },
    { name: 'Thu', trades: 71, volume: 22000 },
    { name: 'Fri', trades: 55, volume: 16000 },
    { name: 'Sat', trades: 30, volume: 8000 },
    { name: 'Sun', trades: 25, volume: 6500 },
  ];

  const userGrowth = Array.from({ length: 12 }, (_, i) => ({
    month: ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][i],
    users: Math.floor(50 + i * 15 + Math.random() * 20),
  }));

  const pieData = [
    { name: 'Active', value: a.active_users || 1, color: '#10B981' },
    { name: 'Blocked', value: Math.max((a.total_users || 1) - (a.active_users || 1), 0), color: '#EF4444' },
  ];

  const tooltipStyle = { background: isDark ? '#1A1A1A' : '#FFF', border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`, borderRadius: 6, color: isDark ? '#fff' : '#0F0F10', fontSize: 12 };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-dashboard-page">
        <div>
          <h1 className="text-xl font-bold font-['Outfit'] text-[var(--text-primary)]">Dashboard Overview</h1>
          <p className="text-sm text-[var(--text-secondary)] mt-1">Platform analytics and key metrics</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {stats.map((s, i) => {
            const Icon = s.icon;
            return (
              <div key={i} className="p-4 bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded-lg hover:border-green-500/30 transition-all duration-200" data-testid={`admin-stat-${s.label.toLowerCase().replace(/ /g, '-')}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${colorMap[s.color]}`}>
                    <Icon className="w-4.5 h-4.5" />
                  </div>
                  {s.change && <span className="text-[10px] font-mono text-emerald-400">{s.change}</span>}
                </div>
                <p className="text-xs text-[var(--text-secondary)] mb-1">{s.label}</p>
                <p className="text-xl font-bold font-mono text-[var(--text-primary)]">{typeof s.value === 'number' ? s.value.toLocaleString() : s.value}</p>
              </div>
            );
          })}
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Trading Activity */}
          <div className="lg:col-span-2 bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded-lg p-5">
            <h3 className="text-sm font-semibold font-['Outfit'] text-[var(--text-primary)] mb-4">Weekly Trading Activity</h3>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={barData}>
                  <XAxis dataKey="name" tick={{ fill: isDark ? '#71717A' : '#9CA3AF', fontSize: 11 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: isDark ? '#71717A' : '#9CA3AF', fontSize: 11 }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={tooltipStyle} />
                  <Bar dataKey="trades" fill="#3B82F6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* User Distribution */}
          <div className="bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded-lg p-5">
            <h3 className="text-sm font-semibold font-['Outfit'] text-[var(--text-primary)] mb-4">User Status</h3>
            <div className="h-44">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={pieData} cx="50%" cy="50%" innerRadius={50} outerRadius={70} paddingAngle={4} dataKey="value">
                    {pieData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                  </Pie>
                  <Tooltip contentStyle={tooltipStyle} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex items-center justify-center gap-4 mt-2">
              {pieData.map((p, i) => (
                <div key={i} className="flex items-center gap-1.5 text-xs text-[var(--text-secondary)]">
                  <div className="w-2.5 h-2.5 rounded-full" style={{ background: p.color }} />
                  {p.name}: {p.value}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* User Growth */}
        <div className="bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded-lg p-5">
          <h3 className="text-sm font-semibold font-['Outfit'] text-[var(--text-primary)] mb-4">User Growth (12 Months)</h3>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={userGrowth}>
                <defs>
                  <linearGradient id="growthGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#3B82F6" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="#3B82F6" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="month" tick={{ fill: isDark ? '#71717A' : '#9CA3AF', fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: isDark ? '#71717A' : '#9CA3AF', fontSize: 11 }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={tooltipStyle} />
                <Area type="monotone" dataKey="users" stroke="#3B82F6" strokeWidth={2} fill="url(#growthGrad)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
